import { getCurrentLayout } from "../../../../../appLayout";
import store from "../../../../../../store";
import {getWizardContexts, getWizardGroupsFromTheWizard} from "../../../../helpers/sqftHelperUi";
import {
  SQFT_PARENT_FEATURE_GROUP_PART_IDS,
  SQFT_PART_IDS_WITH_NAV_ITEMS_FROM_FEATURE_GROUP,
  SQFT_BUTTONS_PART_IDS_NEVER_REMOVE
} from "../../../../constants";
import {hasDisplayedCabinetStyleNavItems} from "../../../../helpers/cabinetStyleNavigationHelper";

const getCurrentWizardItemId = () => {
  return store.getState().wizardState.wizardActiveItem?.id ?? null;
};

const hasSelectedWizardGroup = (wizardContexts, featureGroupNames) => {
  return wizardContexts.some(wizardContext => {
    return Object.values(wizardContext.wizardSelectedValues || {}).some(selectedValue => {
      const groupName = selectedValue?.wizardGroup?.groupName;
      return groupName && featureGroupNames.includes(groupName);
    });
  });
};

const findMatchingWizardGroup = (wizardSteps, featureGroupNames) => {
  for (const wizStep of wizardSteps) {
    for (const wizardGroup of wizStep.stepGroups || []) {
      if (!wizardGroup.groupName || !featureGroupNames.includes(wizardGroup.groupName)) {
        continue;
      }
      return wizardGroup;
    }
  }

  return null;
};

/**
 * Resolves wizard steps for feature-group names derived from menu deps.
 * Used as a fallback when we need to know whether a button can be reached in wizard flow.
 */
const getWizardStepsForAllowedPartIds = (allowedPartIds, useAllWizards) => {
  const layout = getCurrentLayout();
  const featureGroupNames = allowedPartIds.reduce((total, partId) => {
    const featureGroup = layout.getFeatureGroupByPartId(partId);
    if (!featureGroup) return total;
    const groupName = featureGroup.dbName || featureGroup.partName;
    if (!total.includes(groupName)) {
      total.push(groupName);
    }
    return total;
  }, []);

  const wizardSteps = featureGroupNames.reduce((total, groupName) => {
    return [
      ...total,
      ...getWizardGroupsFromTheWizard(groupName, null, null, null, null, useAllWizards, true)
    ];
  }, []);

  return {featureGroupNames, wizardSteps};
};

/**
 * Checks whether menu deps are represented in currently available wizard steps.
 *
 * Matching rules:
 * 1. If a related wizardGroup is already selected in the current wizard, return true.
 * 2. Otherwise split resolved steps into the current wizard and the rest.
 * 3. Check current wizard first.
 * 4. If a group is found there, decide by its `notRequired` value.
 * 5. If the group is already selected in another wizard, return true.
 * 6. Only if the group is not found in current wizard, fall back to the remaining wizards.
 * 7. If the group is not found in any wizard at all, do not block the button.
 *
 * This mirrors wizard switching behavior without actually switching the UI:
 * `switchToWizardItem` restores values for a wizard, then calculates steps for it.
 * `getWizardGroupsFromTheWizard(..., useAllWizards)` does the same resolution for us.
 */
const hasWizardMatch = (allowedPartIds, wizardSteps, useAllWizards = false) => {
  if (!Array.isArray(allowedPartIds) || allowedPartIds.length === 0) {
    return true;
  }

  const resolvedWizardData = getWizardStepsForAllowedPartIds(allowedPartIds, useAllWizards);
  const {featureGroupNames} = resolvedWizardData;
  const state = store.getState();
  const wizardContexts = getWizardContexts(state, null, useAllWizards);
  const currentWizardItemId = getCurrentWizardItemId();

  const resolvedWizardSteps = useAllWizards || !Array.isArray(wizardSteps)
    ? resolvedWizardData.wizardSteps
    : wizardSteps;
  const currentWizardContexts = wizardContexts.filter(context => context.wizardItem?.id === currentWizardItemId);
  const otherWizardContexts = wizardContexts.filter(context => context.wizardItem?.id !== currentWizardItemId);
  const currentWizardSteps = resolvedWizardSteps.filter(wizStep => wizStep.wizardItemId === currentWizardItemId);
  const otherWizardSteps = resolvedWizardSteps.filter(wizStep => wizStep.wizardItemId !== currentWizardItemId);

  if (hasSelectedWizardGroup(currentWizardContexts, featureGroupNames)) {
    return true;
  }

  const currentWizardGroup = findMatchingWizardGroup(currentWizardSteps, featureGroupNames);
  if (currentWizardGroup) {
    return !!currentWizardGroup.notRequired;
  }

  if (hasSelectedWizardGroup(otherWizardContexts, featureGroupNames)) {
    return true;
  }

  const otherWizardGroup = findMatchingWizardGroup(otherWizardSteps, featureGroupNames);
  if (otherWizardGroup) {
    return !!otherWizardGroup.notRequired;
  }

  // If no related wizard group was found at all, do not hide/disable the button by wizard gating.
  return true;
};

/**
 * Determines if a button should be removed or disabled based on fetched feature-data deps.
 * - Remove if a box dependency's parent feature group does not exist in the current layout
 * - Remove if no feature-group nav items exist across deps (unless the dep is in SQFT_BUTTONS_PART_IDS_NEVER_REMOVE)
 * - Never remove buttons whose deps are in SQFT_BUTTONS_PART_IDS_NEVER_REMOVE (e.g. radius, corners) — even if no features exist
 * - Disable if feature-group nav items exist but the display-source nav items are empty
 * - If wizard is active and display-source nav items exist, but no matching selected/reachable wizard group exists -> disable not_available
 */
export const getButtonAvailability = ({
  featuresData,
  deps,
  wizardIsActive,
  wizardSteps,
  useAllWizards = false,
}) => {
  const depKeys = Array.isArray(deps) ? deps : [];
  const layout = getCurrentLayout();
  const requiredParentPartIds = Array.from(new Set(depKeys.map(partId => {
    return SQFT_PARENT_FEATURE_GROUP_PART_IDS[partId];
  }).filter(Boolean)));
  const hasMissingRequiredFeatureGroup = requiredParentPartIds.some(partId => {
    return !layout.getFeatureGroupByPartId(partId);
  });

  if (hasMissingRequiredFeatureGroup) {
    return { shouldRemove: true, isDisabled: false, disableReason: "not_available" };
  }

  const items = depKeys.map(partId => {
    const data = featuresData?.[partId];

    // Some SQFT menus should display the featureGroup list directly ignoring the wizard.
    let sourceNavItems;
    if (SQFT_PART_IDS_WITH_NAV_ITEMS_FROM_FEATURE_GROUP.includes(partId)) {
      sourceNavItems = data?.navItemsFromFeatureGroupFiltered || [];
    } else {
      sourceNavItems = data?.navItemsFromTheWizardFiltered || [];
    }

    const hasNavItems = hasDisplayedCabinetStyleNavItems(partId, data);
    const wasFilteredByCabinetStyleModels = sourceNavItems.length > 0 && !hasNavItems;
    const wasFilteredByAR3D = SQFT_PART_IDS_WITH_NAV_ITEMS_FROM_FEATURE_GROUP.includes(partId)
      ? Boolean(data?.wasFilteredByAR3DFromFeatureGroup)
      : Boolean(data?.wasFilteredByAR3DFromTheWizard);

    return { partId, data, hasNavItems, wasFilteredByAR3D, wasFilteredByCabinetStyleModels };
  }).filter(item => Boolean(item.data));

  const hasAnyAllNavItems = items.some(item => item?.data?.navItemsFromFeatureGroup?.length > 0);
  if (!hasAnyAllNavItems) {
    const neverRemove = depKeys.some(partId => SQFT_BUTTONS_PART_IDS_NEVER_REMOVE.includes(partId));
    if (neverRemove) {
      return { shouldRemove: false, isDisabled: false, disableReason: "not_available" };
    }
    return { shouldRemove: true, isDisabled: false, disableReason: "not_available" };
  }

  const hasAnyNavItems = items.some(item => item.hasNavItems);

  if (!hasAnyNavItems) {
    const wasFilteredByCabinetStyleModels = items.some(item => item.wasFilteredByCabinetStyleModels);
    if (wasFilteredByCabinetStyleModels) {
      const neverRemove = depKeys.some(partId => SQFT_BUTTONS_PART_IDS_NEVER_REMOVE.includes(partId));
      return {
        shouldRemove: !neverRemove,
        isDisabled: neverRemove,
        disableReason: "missing_3d_models",
      };
    }

    const wasFilteredByAR3D = items.some(item => item.wasFilteredByAR3D);
    const disableReason = wasFilteredByAR3D ? "missing_3d_models" : "not_available";
    return { shouldRemove: false, isDisabled: true, disableReason };
  }

  const usesFeatureGroupNavItems = depKeys.some(partId =>
    SQFT_PART_IDS_WITH_NAV_ITEMS_FROM_FEATURE_GROUP.includes(partId)
  );

  if (wizardIsActive && !usesFeatureGroupNavItems) {
    const match = hasWizardMatch(depKeys, wizardSteps, useAllWizards);
    if (!match) {
      return { shouldRemove: false, isDisabled: true, disableReason: "not_available" };
    }
  }

  return { shouldRemove: false, isDisabled: false, disableReason: "not_available" };
};
